import { Observable } from 'rxjs';
import { PermissionVerbsV1 } from '../enums/PermissionVerbsV1';
import { PermissionVerbsV2 } from '../enums/PermissionVerbsV2';
import { BooleanOperator } from '../enums/boolean-operator';

export abstract class PermissionService {
  /**
   * Returns an Observable stream emitting whether the current user is allowed to
   * perform the given action.
   *
   * @param action The action to check for
   * @param spaceUri space uri to check actions for
   * @param tenantUri tenant uri to check actions for
   */
  public abstract userHasPermission(
    action: PermissionVerbsV1 | PermissionVerbsV2 | string,
    spaceUri?: string,
    tenantUri?: string
  ): Observable<boolean>;

  /**
   * Returns an Observable stream emitting whether the current user is allowed to
   * perform all the specified actions.
   *
   * @param actions List of actions to check for
   * @param spaceUri space uri to check actions for
   * @param tenantUri tenant uri to check actions for
   */
  public abstract userHasPermissions(
    actions: Array<PermissionVerbsV1 | PermissionVerbsV2 | string>,
    spaceUri?: string,
    tenantUri?: string
  ): Observable<boolean>;

  /**
   * Returns an Observable stream emitting whether the current user is allowed to
   * perform at least one of the specified actions.
   *
   * @param actions List of actions to check for
   * @param spaceUri space uri to check actions for
   * @param tenantUri tenant uri to check actions for
   */
  public abstract userHasAnyPermission(
    actions: Array<PermissionVerbsV1 | PermissionVerbsV2 | string>,
    spaceUri?: string,
    tenantUri?: string
  ): Observable<boolean>;

  /**
   * Returns an Observable stream emitting whether the current user is allowed to
   * perform at least one of the specified actions in the element type.
   *
   * @param actions List of actions to check for
   * @param elementTypeUri elementType uri to check actions for
   * @param spaceUri space uri to check actions for
   * @returns
   */
  public abstract userHasAnyElementTypePermissions(
    actions: Array<PermissionVerbsV1 | PermissionVerbsV2 | string>,
    elementTypeUri: string,
    spaceUri?: string
  ): Observable<boolean>;

  /**
   * Returns an Observable stream emitting whether the current user is allowed to
   * perform all the specified actions in the element type.
   *
   * @param actions List of actions to check for
   * @param elementTypeUri elementType uri to check actions for
   * @param spaceUri space uri to check actions for
   * @returns
   */
  public abstract userHasElementTypePermissions(
    actions: Array<PermissionVerbsV1 | PermissionVerbsV2 | string>,
    elementTypeUri: string,
    spaceUri?: string
  ): Observable<boolean>;

  /**
   *
   * @param action The action to check for
   * @param elementTypeUri elementType uri to check actions for
   * @param spaceUri space uri to check actions for
   * @returns
   */
  public abstract userHasElementTypePermission(
    action: PermissionVerbsV1 | PermissionVerbsV2 | string,
    elementTypeUri: string,
    spaceUri?: string
  ): Observable<boolean>;

  /**
   * Returns an Observable stream emitting whether the current user is allowed to
   * perform specified action in either tenant level or space level or in any one of the element type 
   *
   * @param action Action to check for
  
   */
  public abstract checkPermissionTenantSpaceAnyType(action: PermissionVerbsV1 | PermissionVerbsV2 | string): Observable<boolean>;

  /**
   * Returns an Observable stream emitting whether the current user is allowed to
   * perform all/any the specified actions in either tenant level or space level or in any one of the element type
   *
   * @param actions List of actions to check for
   * @param mode Boolean operator indicating wether to check all or any permissions
   * @returns
   */
  public abstract checkPermissionsTenantSpaceAnyType(
    actions: Array<PermissionVerbsV1 | PermissionVerbsV2 | string>,
    mode: BooleanOperator
  ): Observable<boolean>;
}
